// Visitor Enhancement / Identification Service
// Checks if current visitor has been identified and returns their info
// Respects user consent preferences for CCPA/GDPR compliance

import { supabase } from '@/integrations/supabase/client';
import { hasVisitorIdConsent } from '@/utils/consentLogging';

interface EnhancedVisitor {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  confidence?: number;
  identifiedAt?: string;
}

interface EnhancementWindow extends Window {
  capturify?: {
    getVisitorId?: () => string | undefined;
    getVisitorData?: () => Record<string, unknown> | undefined;
    disable?: () => void;
    enable?: () => void;
  };
}

// Get anonymous ID from localStorage (must match tracking.ts key)
function getAnonymousId(): string | null {
  try {
    return localStorage.getItem('bv_anonymous_id');
  } catch {
    return null;
  }
}

// Check if visitor identification is allowed based on consent
export function isVisitorIdEnabled(): boolean {
  return hasVisitorIdConsent();
}

// Check if the enhancement provider has identified the current visitor
// Returns null if user has opted out of visitor identification
export async function getIdentifiedVisitor(): Promise<EnhancedVisitor | null> {
  // Check consent first - if user opted out, don't return their data
  if (!isVisitorIdEnabled()) {
    console.log('📍 Visitor identification disabled by user preference');
    return null;
  }

  const anonymousId = getAnonymousId();
  if (!anonymousId) return null;

  try {
    // Query visitor_profiles table for this anonymous ID
    const { data, error } = await supabase
      .from('visitor_profiles')
      .select('email, first_name, last_name, phone, company, identification_confidence, identified_at')
      .eq('anonymous_id', anonymousId)
      .maybeSingle();

    if (error || !data) {
      console.log('📍 No enhanced profile found for anonymous ID');
      return null;
    }

    console.log('✅ Visitor identified:', data.email);
    
    return {
      email: data.email || undefined,
      firstName: data.first_name || undefined,
      lastName: data.last_name || undefined,
      phone: data.phone || undefined,
      company: data.company || undefined,
      confidence: data.identification_confidence || undefined,
      identifiedAt: data.identified_at || undefined,
    };
  } catch (error) {
    console.error('Error fetching enhanced profile:', error);
    return null;
  }
}

// Check if visitor is identified (quick boolean check)
// Returns false if user has opted out
export async function isVisitorIdentified(): Promise<boolean> {
  if (!isVisitorIdEnabled()) return false;
  const visitor = await getIdentifiedVisitor();
  return visitor !== null && !!visitor.email;
}

// Get enhancement provider visitor ID (if available)
// Returns null if user has opted out
export function getEnhancementVisitorId(): string | null {
  if (!isVisitorIdEnabled()) return null;
  
  try {
    const enhancementWindow = window as EnhancementWindow;
    if (enhancementWindow.capturify?.getVisitorId) {
      return enhancementWindow.capturify.getVisitorId() || null;
    }
  } catch (error) {
    console.log('Enhancement provider not loaded or no visitor ID');
  }
  return null;
}

// Disable enhancement tracking (called when user opts out)
export function disableEnhancementTracking(): void {
  try {
    const enhancementWindow = window as EnhancementWindow;
    if (enhancementWindow.capturify?.disable) {
      enhancementWindow.capturify.disable();
      console.log('🚫 Enhancement tracking disabled');
    }
  } catch (error) {
    console.log('Could not disable enhancement tracking:', error);
  }
}

// Enable enhancement tracking (called when user opts in)
export function enableEnhancementTracking(): void {
  try {
    const enhancementWindow = window as EnhancementWindow;
    if (enhancementWindow.capturify?.enable) {
      enhancementWindow.capturify.enable();
      console.log('✅ Enhancement tracking enabled');
    }
  } catch (error) {
    console.log('Could not enable enhancement tracking:', error);
  }
}

// Link anonymous ID to enhancement visitor (called when webhook identifies visitor)
export async function linkAnonymousToEnhancementId(
  enhancementId: string,
  anonymousId: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('visitor_profiles')
      .update({ anonymous_id: anonymousId })
      .eq('capturefy_id', enhancementId);

    if (error) {
      console.error('Error linking anonymous ID to enhancement:', error);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error in linkAnonymousToEnhancementId:', error);
    return false;
  }
}
